import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import SyncIcon from '@mui/icons-material/Sync'
import {
  Button,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { sdk } from '../../../config'
import useGetSurchargePercentage from '../../../hooks/useGetSurchargePercentage/useGetSurchargePercentage'
import {
  checkRepairOrderClosedRecently,
  useTriggerCodatFunctionWithNotification
} from '../../../hooks/useTriggerCodatFunctionWithNotification/useTriggerCodatFunctionWithNotification'
import { lastPaymentDate } from '../../../models'
import { AuthContext } from '../../../providers'
import {
  useGetRepairOrdersQuery,
  useListPaymentsForRepairOrderQuery
} from '../../../redux/api'
import { RepairPaymentInfoCachedFinixTransferStatusEnum } from '../../../sdk'
import { formatCurrency } from '../../../utils'
import { loggingAssert } from '../../../utils/handleError/loggingAssert'
import { roundToTwoDecimal } from '../../../utils/roundToTwoDecimal/roundToTwoDecimal'
import {
  getDueTotalFromPayments,
  isDueTotalZero
} from '../../RepairOrder/getDueTotalFromPayments'
import { usePaymentDialog } from '../RepairOrderPayment/PaymentDialog'

interface Props {
  repairOrderId: string
}

const PaymentPanel: FC<Props> = ({ repairOrderId }) => {
  const { currentUser, userShopId } = useContext(AuthContext)
  const accessToken = loggingAssert(
    'Access token not found',
    currentUser?.accessToken
  )
  const shopId = loggingAssert('Shop ID not found', userShopId)
  const [openFinixDialog, setOpenFinixDialog] = useState(false)

  const {
    setCustomProps: setPaymentProps,
    PaymentDialogUI,
    customProps: paymentDialogCustomProps
  } = usePaymentDialog()

  const { data: repairOrderList } = useGetRepairOrdersQuery({
    authorization: accessToken,
    shop: shopId,
    repairOrder: [repairOrderId]
  })

  const repairOrder = repairOrderList && repairOrderList[0]

  const { surchargePercentage } = useGetSurchargePercentage(
    repairOrder?.entry?.info?.taxesAndFees
  )

  const { data: payments } = useListPaymentsForRepairOrderQuery(
    {
      authorization: accessToken,
      shop: shopId,
      repairOrder: [repairOrder?.entry!.id!]
    },
    {
      skip: !repairOrder,
      pollingInterval:
        paymentDialogCustomProps === undefined || openFinixDialog
          ? undefined
          : 1000
    }
  )

  const dueTotal = useMemo(
    () =>
      getDueTotalFromPayments(payments, repairOrder?.authorizedPricing?.total),
    [payments, repairOrder?.authorizedPricing?.total]
  )

  const dueTotalWithSurcharge = useMemo(() => {
    if (
      dueTotal === undefined ||
      dueTotal <= 0 ||
      surchargePercentage === undefined
    ) {
      return dueTotal
    }
    return roundToTwoDecimal(dueTotal + (dueTotal * surchargePercentage) / 100)
  }, [dueTotal, surchargePercentage])

  const [isSubmittingCardPresent, setIsSubmittingCardPresent] = useState('')

  useEffect(() => {
    if (isSubmittingCardPresent !== '' && isSubmittingCardPresent !== '-1') {
      const payment = payments?.find((p) => p.id === isSubmittingCardPresent)
      if (
        payment?.info?.cachedFinixTransferStatus ===
        RepairPaymentInfoCachedFinixTransferStatusEnum.Succeeded
      ) {
        setIsSubmittingCardPresent('1')
      }
      if (
        payment?.info?.cachedFinixTransferStatus !== undefined &&
        payment?.info?.cachedFinixTransferStatus !==
          RepairPaymentInfoCachedFinixTransferStatusEnum.Succeeded
      ) {
        setIsSubmittingCardPresent('-1')
      }
    }
  }, [isSubmittingCardPresent, payments])

  const repairOrderPricing = repairOrder
    ? repairOrder.entry!.info!.isEstimate
      ? repairOrder.totalPricing
      : repairOrder.authorizedPricing
    : undefined

  const estimateTotal = repairOrderPricing?.total || 0

  const { triggerCodatFunction, loadingTriggerCodat } =
    useTriggerCodatFunctionWithNotification()

  const repairOrderIsClosedAndRecent = useMemo(() => {
    return checkRepairOrderClosedRecently(repairOrder)
  }, [repairOrder])

  const failedCodatPaymentId = useMemo(() => {
    if (!payments) {
      return
    }
    for (let i = 0; i < payments.length; i++) {
      const payment = payments[i]
      if (payment.info?.codatId === 'Failed') {
        return payment.id
      }
    }
  }, [payments])

  const retrySyncToCodat = useCallback(async () => {
    if (failedCodatPaymentId) {
      await sdk.codatRepairPaymentCreation({
        codatRepairPaymentCreationRequest: {
          paymentId: failedCodatPaymentId,
          codatId: undefined,
          codatPushKey: undefined
        }
      })
    }
    await triggerCodatFunction({
      authorization: accessToken,
      shop: shopId
    })
  }, [accessToken, shopId, triggerCodatFunction, failedCodatPaymentId])

  return (
    <>
      <Dialog open={paymentDialogCustomProps !== undefined} fullWidth={true}>
        <PaymentDialogUI
          payments={payments}
          dueTotal={dueTotal}
          dueTotalWithSurcharge={dueTotalWithSurcharge}
          surchargePercentage={surchargePercentage}
          openFinixDialog={openFinixDialog}
          setOpenFinixDialog={setOpenFinixDialog}
          isSubmittingCardPresent={isSubmittingCardPresent}
          setIsSubmittingCardPresent={setIsSubmittingCardPresent}
        />
      </Dialog>
      {dueTotal !== undefined && !repairOrder?.entry!.info!.isEstimate && (
        <Paper variant="outlined" data-testid="PaymentPanel">
          <Stack px={2} pb={2} spacing={2}>
            <TableContainer>
              <Table sx={{ '& td,th': { padding: '16px 0px 2px 0px' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h4">Balance</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={2}>
                        {repairOrderIsClosedAndRecent &&
                          failedCodatPaymentId !== undefined && (
                            <Tooltip title="Retry Quickbooks Payment Sync">
                              <span>
                                <IconButton
                                  onClick={retrySyncToCodat}
                                  disabled={loadingTriggerCodat}>
                                  <SyncIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        <Button
                          onClick={() => {
                            setPaymentProps({
                              repairOrderId: repairOrder!.entry!.id!,
                              counterSaleId: undefined
                            })
                          }}
                          variant="text"
                          startIcon={<AddCircleOutlineIcon />}
                          sx={{ whiteSpace: 'nowrap' }}
                          data-testid="payNowButton">
                          Payment
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isDueTotalZero(dueTotal) ? (
                    payments && payments.length !== 0 ? (
                      <TableRow>
                        <TableCell>Paid</TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          {formatCurrency(estimateTotal, true)}
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2}>No payment history</TableCell>
                      </TableRow>
                    )
                  ) : (
                    <TableRow>
                      <TableCell>Waiting Payment</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>
                        {formatCurrency(dueTotal, true)}
                      </TableCell>
                    </TableRow>
                  )}
                  {payments && payments.length !== 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        {lastPaymentDate(payments)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      )}
    </>
  )
}

export default PaymentPanel
